import { CaseRunCalculateModel, CaseRunCreateModel, ParameterTypes, RunParameterModel } from "@app/api2/api";
import { TranslationContext } from "../Contexts/TranslationContext";
import { Field, Formik, FormikProps } from "formik";
import * as React from "react";
import { Button, Checkbox, Form, FormField, Icon, Message, MessageHeader, MessageItem, MessageList, Segment, Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow } from "@/components/bp/bp-ui";
import { buildParametersValidationSchema } from "./ParameterField";
import { IParameterRenderer } from "./ParameterRendererAbstracts";
import { BPButton } from "@/components/bp/bpbutton";
import { BPCheckbox } from "@/components/bp/bpcheckbox";




class ParametersState {
    initialValues: {}
    showSecondary: boolean = false
}

interface IParametersProps {
    model: { parameters?: RunParameterModel[] };
    aic: boolean;
    onComplete: (values: any) => void;
    // router: any;
    disableStutter: boolean;
    parameterRenderer: IParameterRenderer;
    handler?: { onPropertyChange: (name: string) => void }
    secondaryParametersCollapsed?: boolean;
}

export class Parameters extends React.Component<IParametersProps, ParametersState> {
    private validationSchema = {};
    public form = React.createRef<FormikProps<any>>();

    constructor(props, context) {
        super(props, context);
        this.state = {
            initialValues: {},
            showSecondary: false
        };
        //console.log(`disableStutter ${this.props.disableStutter}`);
        for (let p of this.props.model.parameters) {
            this.state.initialValues[p.parameter] = p.value;

            if (this.props.disableStutter && p.parameter === 'useStutter') {
                this.state.initialValues[p.parameter] = "0";
            }
        }

        // Build validation schema from parameter metadata
        this.validationSchema = buildParametersValidationSchema(this.props.model.parameters as RunParameterModel[]);

    }

    isParameterDisabled = (p: RunParameterModel) => {
        if (this.props.disableStutter && p.parameter === 'useStutter') {
            return true;
        }

        if (this.props.aic && !p.aicEditable) {
            return true;
        }

        if (!p.userEditable) {
            return true;
        }

        return false;
    }

    /**
     * Determines if a parameter row should be visible based on the showIfChecked string.
     * showIfChecked format: comma-separated list of field names, optionally prefixed with '!' for negation,
     * or with '=' for equality checks (e.g. "foo,!bar,baz=2").
     */
    isParameterVisible = (p: RunParameterModel, values) => {
        if (!p.userVisible) {
            return false;
        }

        if (!p.showIfChecked) {
            return true;
        }

        return this.evaluateShowIfChecked(p, values);

    }

    evaluateShowIfChecked = (p: RunParameterModel, values: any) => {
        const { equalFields, negatedFields, trueFields } = this.parseShowIfChecked(p.showIfChecked);

        return negatedFields.reduce((show, field) => show && !(values[field] === "1"), true)
            && trueFields.reduce((show, field) => show && (values[field] === "1"), true)
            && equalFields.reduce((show, field) => show && (values[field[0]] == field[1]), true);
    }


    /**
     * Helper to parse showIfChecked string into trueFields, falseFields, and equalFields.
     * @param showIfChecked string
     * showIfChecked format: comma-separated list of field names, optionally prefixed with '!' for negation,
     * or with '=' for equality checks (e.g. "foo,!bar,baz=2").
     */
    parseShowIfChecked = (showIfChecked: string) => {
        const fields = showIfChecked.split(',');

        const equalFields = fields.filter(x => x.indexOf('=') > -1).map(x => x.split('='));
        const negatedFields = fields.filter(f => f.startsWith("!") && f.indexOf('=') == -1).map(m => m.slice(1));
        const trueFields = fields.filter(f => !f.startsWith("!") && f.indexOf('=') == -1);

        return { equalFields, negatedFields, trueFields };
    }

    renderParameter = ({runparam, values, onParameterChange, errors, setFieldValue}: {runparam: RunParameterModel, values: any, onParameterChange: (p: { parameter: string; }, value: any) => void, errors: any, setFieldValue: any}) => {
        const hasCustomRenderer = this.props.parameterRenderer && this.props.parameterRenderer.canRenderParameter(runparam.parameter);

        return (<TranslationContext.Consumer key={`tconsumer-${runparam.parameter}`}>{t =>
            this.isParameterVisible(runparam, values) && <TableRow key={`paramRow-${runparam.parameter}`}>
                <TableCell verticalAlign={'top'} width={"five"}>{t.parameter.names[runparam.parameter]}</TableCell>
                <TableCell>
                    {hasCustomRenderer &&
                        <FormField error={!!errors[runparam.parameter]}>
                            {this.props.parameterRenderer.renderParameterEditor(this.props.model as CaseRunCreateModel || this.props.model as CaseRunCalculateModel, runparam, values, onParameterChange, errors, setFieldValue, this)}
                        </FormField>}
                    {(!hasCustomRenderer && (runparam.parameterType === ParameterTypes.Number || runparam.parameterType === ParameterTypes.Integer || runparam.parameterType === ParameterTypes.String || runparam.parameterType === ParameterTypes.Json)) &&
                        <FormField error={!!errors[runparam.parameter]}>
                            <Field name={runparam.parameter}
                                disabled={this.isParameterDisabled(runparam)} />
                        </FormField>}
                    {(!hasCustomRenderer && runparam.parameterType === ParameterTypes.Boolean) &&
                        <FormField error={!!errors[runparam.parameter]}>
                            <BPCheckbox name={runparam.parameter} checked={values[runparam.parameter] === "1"}
                                onChange={(e, d) => {
                                    onParameterChange(runparam, d.checked);

                                }}
                                disabled={this.isParameterDisabled(runparam)} />
                        </FormField>}
                </TableCell>
            </TableRow>}</TranslationContext.Consumer>);
    }

    formRender = ({ values, errors, handleSubmit, setFieldValue }: FormikProps<any>) => {
        const onParameterChange = async (p: { parameter: string; }, value: any) => {
            if (typeof value == "boolean")
                await setFieldValue(p.parameter, value ? "1" : "0");
            else
                await setFieldValue(p.parameter, value);

            // if (this.props.parameterRenderer.onParameterValueChange)
            //     this.props.parameterRenderer.onParameterValueChange(p, value, onPrameterChange, this)
        }
        return (<TranslationContext.Consumer>{t =>
            <Form onSubmit={handleSubmit}>
                <Table definition striped>
                    <TableHeader>
                        <TableRow>
                            <TableHeaderCell>{t.common.parameter}</TableHeaderCell>
                            <TableHeaderCell>{t.common.value}</TableHeaderCell>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {this.props.model.parameters.filter(x => !this.props.secondaryParametersCollapsed || !x.isSecondary).map((p) => (
                            this.renderParameter({runparam: p, values, onParameterChange, errors, setFieldValue})
                            // <this.renderParameter key={`r_${p.parameter}`} runparam={p} values={values} onParameterChange={onParameterChange} errors={errors} setFieldValue={setFieldValue} />
                        ))}
                    </TableBody>
                    {this.props.secondaryParametersCollapsed && this.props.model.parameters.filter(x => x.isSecondary).length > 0 &&
                        <TableBody className="secondary">
                            <TableRow>
                                <TableCell colSpan={2}>
                                    <a onClick={() => {
                                        this.setState({ ...this.state, showSecondary: !this.state.showSecondary }); return false;
                                    }}><span>Advanced options</span>
                                        <Icon name={this.state.showSecondary ? 'caret down' : 'caret right'}></Icon>
                                    </a>
                                </TableCell>
                            </TableRow>
                            {this.state.showSecondary && this.props.model.parameters.filter(x => x.isSecondary).map((p) => (
                                this.renderParameter({runparam: p, values, onParameterChange, errors, setFieldValue})
                            ))}
                        </TableBody>
                    }
                </Table>
                {Object.keys(errors).length > 0 &&
                    <Message negative>
                        <MessageHeader>{t.caseRun.errorsOnForm}</MessageHeader>
                        <MessageList>
                            {Object.keys(errors).map((k) => (<MessageItem key={`err-${k}`}>{errors[k]?.toString() || ''}</MessageItem>))}
                        </MessageList>
                    </Message>
                }
                <Segment basic clearing>
                    <BPButton floated="right" type="submit" positive primary>{t.caseRun.next}</BPButton>
                </Segment>
            </Form>}</TranslationContext.Consumer>);
    };
    onSubmit = (values, bag) => {
        this.props.onComplete(values);
        // this.props.router.history.push("/review");
    };
    render() {
        return (
            <Formik innerRef={this.form} enableReinitialize={false} initialValues={this.state.initialValues} onSubmit={this.onSubmit} validationSchema={this.validationSchema}>
                {(props) => {
                    return this.formRender(props)
                }}
            </Formik>
        );
    }
}